import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModal from "../common/DeleteModal";
import {
  DownloadIcon,
  ReplaceIcon,
  TrashIcon,
  UploadCloudIcon,
  ImageUploadIcon,
} from "./icons";
import config from "../../config.js";
import {
  buildAssignDriverVehiclePayload,
  buildCreateDriverPayload,
  toRequiredInt,
  buildUpdateDriverPayload,
} from "../../utils/driverPayloads";
import { useTranslation } from "react-i18next";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#E8EAEE",
  borderRadius: "10px",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  position: "relative",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    height: "55px",
    "& fieldset": {
      borderColor: "#183059",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "#183059",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#183059",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "16px 24px",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  height: "55px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
    borderWidth: "2px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#183059",
  },
  "& .MuiSelect-select": {
    padding: "16px 24px",
  },
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "55px",
  border: "1px solid #414958",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "20px",
  "&:hover": {
    backgroundColor: "#FBC02D",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F2F2F3",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  border: "1px solid #414958",
  color: "#1F242E",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#F2F2F3",
  },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#cc0000",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(204, 0, 0, 0.05)",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  color: "#647490",
}));

const ImageUploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #183059",
  width: "220px",
  height: "220px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#e8e8e8",
  },
}));

const DocumentUploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed #183059",
  borderRadius: "16px",
  padding: "40px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backgroundColor: "#FFFFFF",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "24px",
  fontWeight: 500,
  color: "#183059",
  marginBottom: "16px",
  marginTop: "24px",
}));

const REQUIRED_ASTERISK_SX = {
  color: "#d32f2f",
  ml: 0.5,
};

const ErrorText = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "12px",
  marginLeft: "4px",
  marginTop: "4px",
}));

const sanitizeAlphaSpace = (value) => value.replace(/[^a-zA-Z\s]/g, "");
const sanitizeAlphaNumericId = (value) => value.replace(/[^a-zA-Z0-9-_]/g, "");
const sanitizeLicenseNumber = (value) =>
  value.replace(/[^a-zA-Z0-9-/]/g, "").toUpperCase();
const sanitizePhoneNumber = (value) => value.replace(/\D/g, "").slice(0, 10);
const sanitizePincode = (value) => value.replace(/\D/g, "").slice(0, 6);

export default function CreateDriverForm({
  onClose,
  onSubmit,
  initialData = null,
  isEdit = false,
  onDelete,
}) {
  const { t } = useTranslation();
  const [organizationId, setOrganizationId] = useState(null);
  const [vehicleData, setVehicleData] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState(
    initialData?.uploadedDocuments?.map((doc) => ({
      ...doc,
      isExisting: true,
      isUploaded: true,
    })) || [],
  );
  const [formData, setFormData] = useState(
    initialData || {
      driverId: "",
      driverName: "",
      email: "",
      phoneNumber: "",
      assignedVehicle: "",
      dlNumber: "",
      profileImage: null,
      presentAddress: "",
      presentCity: "",
      presentPincode: "",
      permanentAddress: "",
      permanentCity: "",
      permanentPincode: "",
      documents: null,
    },
  );

  const [imagePreview, setImagePreview] = useState(
    initialData?.profileImage || null,
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sameAsPresent, setSameAsPresent] = useState(() => {
    // Auto-check if no permanent address data exists
    return (
      !initialData ||
      (!initialData.permanentAddress &&
        !initialData.permanentCity &&
        !initialData.permanentPincode)
    );
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const getDocumentTypeLabel = (type) => t(type);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem("userState"));
    const storedOrgId = storedData?.user?.organizationId;
    if (storedOrgId) {
      setOrganizationId(parseInt(storedOrgId, 10));
    }
  }, []);

  // Auto-fill permanent address with present address when sameAsPresent is true on mount
  useEffect(() => {
    if (sameAsPresent && initialData) {
      setFormData((prev) => ({
        ...prev,
        permanentAddress: prev.presentAddress,
        permanentCity: prev.presentCity,
        permanentPincode: prev.presentPincode,
      }));
    }
  }, [sameAsPresent]);

  // Auto-check "Same as Present" when all present address fields are empty
  useEffect(() => {
    const allPresentEmpty =
      !formData.presentAddress?.trim() &&
      !formData.presentCity?.trim() &&
      !formData.presentPincode?.trim();
    if (allPresentEmpty && !sameAsPresent) {
      setSameAsPresent(true);
    }
  }, [formData.presentAddress, formData.presentCity, formData.presentPincode]);

  useEffect(() => {
    const fetchVehicles = async () => {
      if (!organizationId) return;

      try {
        const response = await axios.get(
          `${config.baseURL}/vehicle/organization`,
          {
            params: { organizationId },
            withCredentials: true,
          },
        );

        const vehicles = response.data || [];
        setVehicleData(
          vehicles.map((v) => ({
            id: v.id,
            name: v.name || v.device_id,
          })),
        );
      } catch (error) {
        console.error("Error fetching vehicles:", error);
        setSubmitError(t("Failed to fetch vehicles"));
      }
    };

    fetchVehicles();
  }, [organizationId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.driverName.trim()) {
      newErrors.driverName = t("Driver Name is required");
    } else if (!/^[a-zA-Z\s]+$/.test(formData.driverName.trim())) {
      newErrors.driverName = t("Driver Name must contain only letters");
    }
    if (!formData.driverId.trim()) {
      newErrors.driverId = t("Driver ID is required");
    } else if (!/^[a-zA-Z0-9-_]+$/.test(formData.driverId.trim())) {
      newErrors.driverId = t(
        "Driver ID can contain only letters, numbers, hyphen, and underscore",
      );
    }
    if (!formData.dlNumber.trim()) {
      newErrors.dlNumber = t("Driving License Number is required");
    } else if (!/^[a-zA-Z0-9-/]+$/.test(formData.dlNumber.trim())) {
      newErrors.dlNumber = t(
        "Driving License Number can contain only letters, numbers, hyphen, and slash",
      );
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t("Phone Number is required");
    } else if (!/^\d{10}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = t("Phone Number must be 10 digits");
    }
    if (!formData.presentAddress.trim()) {
      newErrors.presentAddress = t("Present Address is required");
    }
    if (!formData.presentPincode.trim()) {
      newErrors.presentPincode = t("Present Pincode is required");
    } else if (!/^\d{6}$/.test(formData.presentPincode.trim())) {
      newErrors.presentPincode = t("Present Pincode must be 6 digits");
    }
    if (!formData.presentCity.trim()) {
      newErrors.presentCity = t("Present City is required");
    } else if (!/^[a-zA-Z\s]+$/.test(formData.presentCity.trim())) {
      newErrors.presentCity = t("Present City must contain only letters");
    }

    if (!sameAsPresent) {
      if (
        !formData.permanentAddress?.trim() ||
        !formData.permanentCity?.trim() ||
        !formData.permanentPincode?.trim()
      ) {
        newErrors.permanentCity =
          t('Please fill the permanent address or check "Same as Present Address"');
      } else {
        if (!/^[a-zA-Z\s]+$/.test(formData.permanentCity.trim())) {
          newErrors.permanentCity = t("Permanent City must contain only letters");
        }
        if (!/^\d{6}$/.test(formData.permanentPincode.trim())) {
          newErrors.permanentPincode = t("Permanent Pincode must be 6 digits");
        }
      }
    }

    if (!formData.email?.trim()) {
      newErrors.email = t("Email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = t("Invalid email format");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDocumentUpload = (event, docType) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedDocuments([
          ...uploadedDocuments,
          {
            type: docType,
            image: reader.result,
            file: file,
            isExisting: false,
            isUploaded: false,
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocTypeChange = (index, newType) => {
    const updatedDocs = [...uploadedDocuments];
    updatedDocs[index].type = newType;
    setUploadedDocuments(updatedDocs);
  };

  const handleDocumentDelete = async (index) => {
    const docToRemove = uploadedDocuments[index];

    if (docToRemove.isExisting && docToRemove.isUploaded && initialData?.id) {
      try {
        setLoading(true);
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: docToRemove.type || "Other",
            associateEntityId: initialData.id,
            associatedWith: "driver",
          },
          withCredentials: true,
        });

        console.log(`Successfully deleted document: ${docToRemove.type}`);

      } catch (error) {
        console.error("Error deleting document:", error);
        setSubmitError(
          `${t("Failed to delete document")}: ${
            error.response?.data?.error || error.message
          }`,
        );
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    const updatedDocs = uploadedDocuments.filter((_, i) => i !== index);
    setUploadedDocuments(updatedDocs);
  };

  const handleDocumentReplace = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedDocs = [...uploadedDocuments];
        updatedDocs[index].image = reader.result;
        updatedDocs[index].file = file;
        updatedDocs[index].needsReplacement = updatedDocs[index].isExisting;
        setUploadedDocuments(updatedDocs);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setSubmitError("");
    setFormData({ ...formData, [field]: value });

    // Real-time validation for each field
    let error = "";
    if (field === "driverName") {
      if (!value.trim()) {
        error = t("Driver Name is required");
      } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        error = t("Driver Name must contain only letters");
      }
    }
    if (field === "driverId") {
      if (!value.trim()) {
        error = t("Driver ID is required");
      } else if (!/^[a-zA-Z0-9-_]+$/.test(value.trim())) {
        error = t(
          "Driver ID can contain only letters, numbers, hyphen, and underscore",
        );
      }
    }
    if (field === "dlNumber") {
      if (!value.trim()) {
        error = t("Driving License Number is required");
      } else if (!/^[a-zA-Z0-9-/]+$/.test(value.trim())) {
        error = t(
          "Driving License Number can contain only letters, numbers, hyphen, and slash",
        );
      }
    }
    if (field === "phoneNumber") {
      if (!value.trim()) {
        error = t("Phone Number is required");
      } else if (!/^\d{10}$/.test(value.trim())) {
        error = t("Phone Number must be 10 digits");
      }
    }
    if (field === "presentAddress") {
      if (!value.trim()) {
        error = t("Present Address is required");
      }
    }
    if (field === "presentPincode") {
      if (!value.trim()) {
        error = t("Present Pincode is required");
      } else if (!/^\d{6}$/.test(value.trim())) {
        error = t("Present Pincode must be 6 digits");
      }
    }
    if (field === "presentCity") {
      if (!value.trim()) {
        error = t("Present City is required");
      } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
        error = t("Present City must contain only letters");
      }
    }
    if (field === "permanentCity") {
      if (!sameAsPresent) {
        if (
          !formData.permanentAddress?.trim() ||
          !value.trim() ||
          !formData.permanentPincode?.trim()
        ) {
          error =
            t('Please fill the permanent address or check "Same as Present Address"');
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = t("Permanent City must contain only letters");
        }
      }
    }
    if (field === "permanentPincode") {
      if (!sameAsPresent) {
        if (
          !formData.permanentAddress?.trim() ||
          !formData.permanentCity?.trim() ||
          !value.trim()
        ) {
          error =
            t('Please fill the permanent address or check "Same as Present Address"');
        } else if (!/^\d{6}$/.test(value.trim())) {
          error = t("Permanent Pincode must be 6 digits");
        }
      }
    }
    if (field === "email") {
      if (!value.trim()) {
        error = t("Email is required");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        error = t("Invalid email format");
      }
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleBlur = (field) => () => {
    setTouched({ ...touched, [field]: true });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    if (onDelete) {
      try {
        await onDelete();
      } catch (error) {
        setSubmitError(t("Failed to delete driver"));
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleImageUpload = (event) => {
    setImageError("");
    setSubmitError("");
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setImageError(t("Please upload a valid image file (JPEG, PNG, GIF, or WEBP)"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError(t("Image size should be less than 5MB"));
      return;
    }

    setFormData({
      ...formData,
      profileImage: file,
    });
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleProfileImageDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEdit && initialData?.id && !(formData.profileImage instanceof File)) {
      try {
        setIsSubmitting(true);
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: {
            name: "DriverImage",
            associateEntityId: initialData.id,
            associatedWith: "driver",
          },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting profile image:", error);
        setImageError(
          `${t("Failed to delete image")}: ${error.response?.data?.error || error.message}`,
        );
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    setImageError("");
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, profileImage: null }));
  };

  const handleSameAsPresent = (event) => {
    const checked = event.target.checked;
    setSameAsPresent(checked);

    if (checked) {
      setFormData({
        ...formData,
        permanentAddress: formData.presentAddress,
        permanentCity: formData.presentCity,
        permanentPincode: formData.presentPincode,
      });
      if (errors.permanentCity) {
        setErrors({ ...errors, permanentCity: "" });
      }
    }
  };

  const uploadProfilePicture = async (driverId, file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("image", file);

      const response = await axios.post(
        `${config.baseURL}/upload/image?name=${encodeURIComponent(
          "DriverImage",
        )}&associateEntityId=${driverId}&associatedWith=driver&type=profile`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading driver profile picture:", error);
      throw error;
    }
  };

  const uploadDocument = async (driverId, file, docType, needsReplacement) => {
    try {
      if (needsReplacement) {
        try {
          await axios.delete(`${config.baseURL}/upload/image`, {
            params: {
              name: docType || "Other",
              associateEntityId: driverId,
              associatedWith: "driver",
            },
            withCredentials: true,
          });
        } catch (deleteError) {
          console.warn("Failed to delete old document:", deleteError);
        }
      }

      const formDataUpload = new FormData();
      formDataUpload.append("image", file);

      const response = await axios.post(
        `${config.baseURL}/upload/image?name=${encodeURIComponent(
          docType,
        )}&associateEntityId=${driverId}&associatedWith=driver&type=document`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (error) {
      console.error(`Error uploading document ${docType}:`, error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setTouched({
      driverName: true,
      driverId: true,
      dlNumber: true,
      phoneNumber: true,
      presentAddress: true,
      presentCity: true,
      presentPincode: true,
      email: true,
    });

    if (!validateForm()) {
      setSubmitError("Please fill all required fields correctly");
      return;
    }

    setIsSubmitting(true);

    try {
      const nameParts = formData.driverName.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ") || ".";

      const driverPayloadSource = {
        ...formData,
        organizationId,
        firstName,
        lastName,
        phoneNumber: formData.phoneNumber,
        drivingLicenseId: formData.dlNumber,
        vehicle_id: formData.assignedVehicle,
        presentAddress: formData.presentAddress || "",
        presentCity: formData.presentCity || "",
        presentPincode: formData.presentPincode || "",
        permanentAddress: sameAsPresent
          ? formData.presentAddress || ""
          : formData.permanentAddress || "",
        permanentCity: sameAsPresent
          ? formData.presentCity || ""
          : formData.permanentCity || "",
        permanentPincode: sameAsPresent
          ? formData.presentPincode || ""
          : formData.permanentPincode || "",
        description: "",
      };

      let driverId;
      const editDriverId = toRequiredInt(initialData?.id);

      if (isEdit && initialData?.id) {
        if (editDriverId === null) {
          throw new Error("Invalid driver id");
        }

        const response = await axios.put(
          `${config.baseURL}/driver/${editDriverId}`,
          buildUpdateDriverPayload(driverPayloadSource),
          { withCredentials: true },
        );
        driverId = editDriverId;
      } else {
        const response = await axios.post(
          `${config.baseURL}/driver`,
          buildCreateDriverPayload(driverPayloadSource),
          { withCredentials: true },
        );
        driverId = response.data.id;
      }

      if (formData.profileImage instanceof File) {
        try {
          await uploadProfilePicture(driverId, formData.profileImage);
        } catch (imageError) {
          console.error("Profile picture upload failed:", imageError);
          setImageError("Driver saved but profile picture upload failed");
        }
      }

      for (const doc of uploadedDocuments) {
        if (doc.file instanceof File) {
          try {
            await uploadDocument(
              driverId,
              doc.file,
              doc.type,
              doc.needsReplacement,
            );
          } catch (docError) {
            console.error(`Document upload failed for ${doc.type}:`, docError);
          }
        }
      }

      if (formData.assignedVehicle) {
        try {
          await axios.put(
            `${config.baseURL}/driver/vehicle`,
            buildAssignDriverVehiclePayload(driverId, formData.assignedVehicle),
            { withCredentials: true },
          );
        } catch (vehicleError) {
          console.error("Vehicle assignment failed:", vehicleError);
          setSubmitError("Driver saved but vehicle assignment failed");
        }
      }

      if (!isEdit) {
        setFormData({
          driverId: "",
          driverName: "",
          email: "",
          phoneNumber: "",
          assignedVehicle: "",
          dlNumber: "",
          profileImage: null,
          presentAddress: "",
          presentCity: "",
          presentPincode: "",
          permanentAddress: "",
          permanentCity: "",
          permanentPincode: "",
          documents: null,
        });
        setImagePreview(null);
        setImageError("");
        setSameAsPresent(false);
        setUploadedDocuments([]);
        setTouched({});
        setErrors({});
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      console.error("Submission error:", error);
      const errMsg = error.response?.data?.error || "";
      if (
        errMsg.toLowerCase().includes("already in use") ||
        errMsg.toLowerCase().includes("email")
      ) {
        setErrors((prev) => ({ ...prev, email: errMsg }));
      } else {
      setSubmitError(errMsg || t("Failed to submit driver data"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const UploadedBox = ({ docType, image, index }) => {
    const availableDocTypes = [
      "Photo ID",
      "Driving License ID",
      "Address Proof",
    ].filter((type) => !uploadedDocuments.some((doc) => doc.type === type));

    return (
      <Grid item xs={4}>
        <Box
          sx={{
            maxHeight: "460px",
            width: "auto",
            borderRadius: "16px",
            border: "1px solid #183059",
            padding: "24px",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Select
              displayEmpty
              value={docType}
              onChange={(e) => handleDocTypeChange(index, e.target.value)}
              renderValue={(selected) =>
                selected ? getDocumentTypeLabel(selected) : t("Select Document Type")
              }
              sx={{
                backgroundColor: "#B7BFCC",
                boxShadow: "-2px 2px 5px 0px #00000011",
                height: "48px",
                borderRadius: "8px",
                width: "calc(100% - 60px)",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                color: "#111214",
              }}
            >
              <MenuItem value="" disabled>
                {t("Select Document Type")}
              </MenuItem>
              <MenuItem value={docType}>{getDocumentTypeLabel(docType)}</MenuItem>
              {availableDocTypes
                .filter((type) => type !== docType)
                .map((type) => (
                  <MenuItem key={type} value={type}>
                    {getDocumentTypeLabel(type)}
                  </MenuItem>
                ))}
            </Select>

            <Button
              component="span"
              sx={{
                border: "1px solid #414958",
                padding: "10px 12px",
                height: "48px",
                width: "48px",
                borderRadius: "8px",
                minWidth: "48px",
              }}
            >
              <DownloadIcon />
            </Button>
          </Box>
          <Box
            sx={{
              padding: "10px",
              height: "268px",
              backgroundColor: "#b7bfcc4c",
              borderRadius: "8px",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={image}
              alt={docType}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <input
              accept="image/*,.pdf"
              style={{ display: "none" }}
              id={`replace-doc-${index}`}
              type="file"
              onChange={(e) => handleDocumentReplace(index, e)}
            />
            <label htmlFor={`replace-doc-${index}`}>
              <Button
                component="span"
                sx={{
                  backgroundColor: "#20A8E0",
                  height: "48px",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  gap: "10px",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1890c0",
                  },
                }}
              >
                <ReplaceIcon />
                {t("Change")}
              </Button>
            </label>
            <Button
              onClick={() => handleDocumentDelete(index)}
              sx={{
                backgroundColor: "#EE3B2B",
                height: "48px",
                borderRadius: "8px",
                padding: "12px 24px",
                display: "flex",
                gap: "10px",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#d42a1a",
                },
              }}
            >
              <TrashIcon />
              {t("Delete")}
            </Button>
          </Box>
        </Box>
      </Grid>
    );
  };

  const UploadBox = () => {
    const [selectedType, setSelectedType] = useState("");
    const availableDocTypes = [
      "Photo ID",
      "Driving License ID",
      "Address Proof",
    ].filter((type) => !uploadedDocuments.some((doc) => doc.type === type));

    return (
      <Grid item xs={4}>
        <Box
          sx={{
            maxHeight: "460px",
            width: "auto",
            borderRadius: "16px",
            border: "1px solid #183059",
            padding: "24px",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              renderValue={(selected) =>
                selected ? getDocumentTypeLabel(selected) : t("Select Document Type")
              }
              sx={{
                backgroundColor: "#B7BFCC",
                boxShadow: "-2px 2px 5px 0px #00000011",
                height: "48px",
                borderRadius: "8px",
                width: "100%",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "20px",
                color: "#111214",
              }}
              displayEmpty
            >
              <MenuItem value="" disabled>
                {t("Select Document Type")}
              </MenuItem>
              {availableDocTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {getDocumentTypeLabel(type)}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <input
            accept="image/*,.pdf"
            style={{ display: "none" }}
            id="new-document-upload"
            type="file"
            onChange={(e) => {
              if (selectedType) {
                handleDocumentUpload(e, selectedType);
                setSelectedType("");
              }
            }}
            disabled={!selectedType}
          />
          <label htmlFor="new-document-upload">
            <Box
              sx={{
                padding: "10px",
                height: "268px",
                backgroundColor: "#b7bfcc4c",
                borderRadius: "8px",
                mb: 2,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "#414958",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 20,
                cursor: selectedType ? "pointer" : "not-allowed",
                opacity: selectedType ? 1 : 0.5,
              }}
            >
              {t("+ New Document")}
            </Box>
          </label>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <label htmlFor="new-document-upload">
              <Button
                component="span"
                disabled={!selectedType}
                sx={{
                  backgroundColor: "#20A8E0",
                  height: "48px",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  gap: "10px",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "20px",
                  textTransform: "none",
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                  },
                  "&:hover": {
                    backgroundColor: selectedType ? "#1890c0" : "#ccc",
                  },
                }}
              >
                <UploadCloudIcon />
                {t("Upload")}
              </Button>
            </label>
          </Box>
        </Box>
      </Grid>
    );
  };

  return (
    <FormContainer>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <input
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            style={{ display: "none" }}
            id={`driver-image-upload-${isEdit ? "edit" : "create"}`}
            type="file"
            onChange={handleImageUpload}
          />

          <Box sx={{ position: "relative", width: "220px" }}>
            <label
              htmlFor={`driver-image-upload-${isEdit ? "edit" : "create"}`}
            >
              <ImageUploadBox>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box textAlign="center">
                    <ImageUploadIcon />
                  </Box>
                )}
              </ImageUploadBox>
            </label>

            {imagePreview && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 4,
                  zIndex: 2,
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 1,
                }}
              >
                {/* Edit Icon */}
                <label
                  htmlFor={`driver-image-upload-${isEdit ? "edit" : "create"}`}
                  style={{ cursor: "pointer" }}
                >
                  <svg
                    width="52"
                    height="36"
                    viewBox="0 0 52 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      fill="#F2F2F3"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      stroke="#20A8E0"
                    />
                    <path
                      d="M29.333 9.67676L31.833 12.1768L29.9272 14.0834L27.4272 11.5834L29.333 9.67676ZM19.333 19.6668V22.1668H21.833L28.7488 15.2609L26.2488 12.7609L19.333 19.6668ZM19.333 24.6668H32.6663V26.3334H19.333V24.6668Z"
                      fill="#20A8E0"
                    />
                  </svg>
                </label>

                {/* Delete Icon */}
                <Box
                  component="span"
                  onClick={handleProfileImageDelete}
                  sx={{ cursor: "pointer" }}
                >
                  <svg
                    width="52"
                    height="36"
                    viewBox="0 0 52 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      fill="#F2F2F3"
                    />
                    <rect
                      x="0.5"
                      y="0.5"
                      width="51"
                      height="35"
                      rx="7.5"
                      stroke="#EE3B2B"
                    />
                    <path
                      d="M29.3327 11H26.666L25.3327 9.66699H20.666L19.3327 11H16.666V13.667H29.3327V11ZM17.9993 25.667C17.9993 27.1337 19.1993 28.3337 20.666 28.3337H25.9993C27.466 28.3337 28.666 27.1337 28.666 25.667V14.3337H17.9993V25.667ZM20.666 17.0003H25.9993V25.667H20.666V17.0003Z"
                      fill="#EE3B2B"
                    />
                  </svg>
                </Box>
              </Box>
            )}

            {imageError && (
              <ErrorText sx={{ textAlign: "center", mt: 1 }}>
                {imageError}
              </ErrorText>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                  color: "#1F242E",
                }}
              >
                {t("Full Name")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.driverName}
                onChange={handleChange("driverName")}
                onBlur={handleBlur("driverName")}
                placeholder={t("Driver Name")}
                error={touched.driverName && !!errors.driverName}
                helperText={touched.driverName && errors.driverName}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "20px",
                }}
              >
                {t("Phone Number")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.phoneNumber}
                onChange={handleChange("phoneNumber")}
                placeholder={t("Phone Number")}
                error={touched.phoneNumber && !!errors.phoneNumber}
                helperText={touched.phoneNumber && errors.phoneNumber}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Column 3: Driver ID and Email */}
        <Grid item xs={12} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Driver ID")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.driverId}
                onChange={handleChange("driverId")}
                placeholder={t("Driver ID")}
                error={touched.driverId && !!errors.driverId}
                helperText={touched.driverId && errors.driverId}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Email")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.email}
                onChange={handleChange("email")}
                onBlur={handleBlur("email")}
                placeholder={t("Email")}
                type="email"
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Column 4: Driving License and Assigned Vehicle */}
        <Grid item xs={12} md={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Driving License No.")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.dlNumber}
                onChange={handleChange("dlNumber")}
                placeholder={t("DL Number")}
                error={touched.dlNumber && !!errors.dlNumber}
                helperText={touched.dlNumber && errors.dlNumber}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Assigned Vehicle")}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.assignedVehicle}
                  onChange={handleChange("assignedVehicle")}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {t("Select Vehicle")}
                  </MenuItem>
                  {vehicleData.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Address Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Present Address Column */}
        <Grid item xs={12} md={6}>
          <SectionTitle
            sx={{
              backgroundColor: "#647490",
              boxShadow: "-2px 2px 5px 0px #00000040",
              height: "52px",
              borderRadius: "8px",
              padding: "12px 24px",
              color: "#FFFFFF",
            }}
          >
            {t("Present Address")}
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Address")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.presentAddress}
                onChange={handleChange("presentAddress")}
                onBlur={handleBlur("presentAddress")}
                placeholder={t("Present Address")}
                error={touched.presentAddress && !!errors.presentAddress}
                helperText={touched.presentAddress && errors.presentAddress}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("City")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.presentCity}
                onChange={handleChange("presentCity")}
                placeholder={t("City")}
                error={touched.presentCity && !!errors.presentCity}
                helperText={touched.presentCity && errors.presentCity}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Pin Code")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.presentPincode}
                onChange={handleChange("presentPincode")}
                placeholder={t("Pincode")}
                error={touched.presentPincode && !!errors.presentPincode}
                helperText={touched.presentPincode && errors.presentPincode}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Permanent Address Column */}
        <Grid item xs={12} md={6}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <SectionTitle
              sx={{
                backgroundColor: "#647490",
                boxShadow: "-2px 2px 5px 0px #00000040",
                height: "52px",
                borderRadius: "8px",
                padding: "12px 24px",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {t("Permanent Address")}
              <Box
                sx={{
                  backgroundColor: "#FFFFFF",
                  height: "32px",
                  borderRadius: "4px",
                  display: "flex",
                  gap: "4px",
                  padding: " 4px 8px",
                  color: "#414958",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sameAsPresent}
                      onChange={handleSameAsPresent}
                      sx={{
                        color: "#183059",
                        "&.Mui-checked": {
                          color: "#183059",
                        },
                      }}
                    />
                  }
                  label={t("Same as Present Address")}
                />
              </Box>
            </SectionTitle>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Address")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.permanentAddress}
                onChange={handleChange("permanentAddress")}
                placeholder={t("Permanent Address")}
                disabled={sameAsPresent}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("City")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.permanentCity}
                onChange={handleChange("permanentCity")}
                placeholder={t("City")}
                disabled={sameAsPresent}
                error={!!errors.permanentCity}
                helperText={errors.permanentCity}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontStyle: "SemiBold",
                  fontSize: "20px",
                }}
              >
                {t("Pincode")}
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.permanentPincode}
                onChange={handleChange("permanentPincode")}
                placeholder={t("Pincode")}
                disabled={sameAsPresent}
                error={!!errors.permanentPincode}
                helperText={errors.permanentPincode}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Upload Documents Section */}
      <Box sx={{ mt: 3 }}>
        <SectionTitle
          sx={{
            backgroundColor: "#FFFFFF",
            boxShadow: "-2px 2px 5px 0px #00000040",
            height: "52px",
            borderRadius: "8px",
            padding: "12px 24px",
            color: "#183059",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {t(
            "Upload Documents (Photo ID, Driving License ID, Address Proof)",
          )}
        </SectionTitle>
        <Grid container spacing={2} mt={1}>
          {uploadedDocuments.map((doc, index) => (
            <UploadedBox
              key={index}
              docType={doc.type}
              image={doc.image}
              index={index}
            />
          ))}
          {uploadedDocuments.length < 3 && <UploadBox />}
        </Grid>
      </Box>

      {/* Action Buttons */}
      {submitError && (
        <ErrorText sx={{ textAlign: "center", mt: 4 }}>{submitError}</ErrorText>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={submitError ? 1 : 4}
      >
        <Box>
          {isEdit && onDelete && (
            <DeleteButton
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
            >
              {t("Delete")}
            </DeleteButton>
          )}
        </Box>
        <Box display="flex" gap={8}>
          {!isEdit && <CancelButton onClick={onClose}>{t("Cancel")}</CancelButton>}
          <ConfirmButton onClick={handleSubmit} disabled={isSubmitting}>
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box></Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Driver!")}
        message={t(
          "Are you sure you want to delete this driver? This action cannot be undone.",
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={t("Delete")}
      />
    </FormContainer>
  );
}
